import moment from "moment";
import "moment/locale/es";
import { HeaderEmpresaCard } from "../Components/PdfComponents/HeaderEmpresaCard";
import {
  ETIQUETAS_REDONDEO,
  ETIQUETAS_TRIESTADO,
  ETIQUETAS_BASE_RECALCULO,
} from "../Utils/reglasPrecio";

moment.locale("es");

const layoutTabla = {
  fillColor: (rowIndex) => {
    if (rowIndex === 0) return "#cccccc";
    return rowIndex % 2 === 0 ? "#e8e8e8" : "#ffffff";
  },
  hLineWidth: function () { return 1; },
  vLineWidth: function () { return 1; },
  hLineColor: function () { return "#adaaaa"; },
  vLineColor: function () { return "#adaaaa"; },
};

const layoutSinBordes = {
  hLineWidth: function () { return 0; },
  vLineWidth: function () { return 0; },
};

const dinero = (valor) => {
  if (valor === null || valor === undefined || isNaN(Number(valor))) return "-";
  return (
    "$ " +
    Number(valor)
      .toFixed(2)
      .replace(/\d{1,3}(?=(\d{3})+(?!\d))/g, "$&,")
  );
};

const pct = (valor) =>
  valor === null || valor === undefined || valor === "" ? "-" : `${valor}%`;

const dineroConSigno = (valor) => {
  if (valor === null || valor === undefined || isNaN(Number(valor))) return "-";
  const n = Number(valor);
  return `${n > 0 ? "+" : ""}${dinero(n)}`;
};

const filaVacia = (columnas, texto) => [
  { text: texto, style: "textoTabla", colSpan: columnas, alignment: "center" },
  ...Array(columnas - 1).fill({}),
];

// El caso extremo se describe con el precio y el margen que quedarian, no con
// cuanto sube en porcentaje: ese numero se confunde con el margen.
const textoExtremo = (ejemplo, siNoHay) => {
  if (!ejemplo) return siNoHay;
  return (
    `${ejemplo.descripcion || ""} ${dinero(ejemplo.precioAntes)} a ${dinero(ejemplo.precioDespues)} ` +
    `(${dineroConSigno(ejemplo.diferencia)}, margen ${pct(ejemplo.margenAntes)} a ${pct(ejemplo.margenDespues)})`
  );
};

// Tarjeta de un numero grande con su etiqueta, para el resumen de impacto.
const tarjeta = (etiqueta, valor, color) => ({
  table: {
    widths: ["100%"],
    body: [
      [{ text: String(valor), style: "cifra", color: color || "black", alignment: "center" }],
      [{ text: etiqueta, style: "cifraEtiqueta", alignment: "center" }],
    ],
  },
  layout: layoutSinBordes,
});

const simulacionListasDePreciosPdf = (values) => {
  const empresa = values.empresa;
  const logoBiwo = values.logoBiwo;
  const listas = values.listas || [];

  const contenidoListas = listas.flatMap((lista, indice) => {
    const c = lista.configuracion || {};

    const filasConfig = [
      [
        { text: "Porcentaje de la lista:", style: "subheader" },
        { text: pct(c.porcentaje), style: "textoChico" },
        { text: "Recalcular con:", style: "subheader" },
        {
          text: ETIQUETAS_BASE_RECALCULO[Number(c.baseRecalculo) || 0],
          style: "textoChico",
        },
      ],
      [
        { text: "Rango de margen:", style: "subheader" },
        {
          text:
            c.margenMinimo == null && c.margenMaximo == null
              ? "Sin limites"
              : `${pct(c.margenMinimo)} a ${pct(c.margenMaximo)}`,
          style: "textoChico",
        },
        { text: "Calculo automatico:", style: "subheader" },
        {
          text: ETIQUETAS_TRIESTADO[Number(c.calcularAutomaticamente) || 0],
          style: "textoChico",
        },
      ],
      [
        { text: "Rango de descuento:", style: "subheader" },
        {
          text:
            c.descuentoMinimo == null && c.descuentoMaximo == null
              ? "Sin limites"
              : `${pct(c.descuentoMinimo)} a ${pct(c.descuentoMaximo)}`,
          style: "textoChico",
        },
        { text: "No bajar precios:", style: "subheader" },
        { text: ETIQUETAS_TRIESTADO[Number(c.noBajarPrecios) || 0], style: "textoChico" },
      ],
      [
        { text: "Redondeo:", style: "subheader" },
        { text: ETIQUETAS_REDONDEO[Number(c.redondeo) || 0], style: "textoChico" },
        { text: "Factor de precio piso:", style: "subheader" },
        {
          text: c.factorPrecioPiso == null ? "Sin piso" : `costo x ${c.factorPrecioPiso}`,
          style: "textoChico",
        },
      ],
      [
        { text: "Reglas que la afectan:", style: "subheader" },
        { text: String(lista.reglasQueLaTocan || 0), style: "textoChico" },
        { text: "De la muestra, con precio en esta lista:", style: "subheader" },
        { text: String(lista.conEntrada || 0), style: "textoChico" },
      ],
    ];

    const filasMotivos = (lista.motivos || []).length
      ? lista.motivos.map((m) => [
          { text: String(m.cuantos), style: "textoTabla", alignment: "right" },
          { text: m.motivo, style: "textoTabla" },
        ])
      : [filaVacia(2, "Todos los insumos de esta lista se pudieron recalcular")];

    const encabezadoEjemplos = [
      { text: "Clave", style: "tituloTabla" },
      { text: "Descripcion", style: "tituloTabla" },
      { text: "Costo", style: "tituloTabla" },
      { text: "Margen hoy", style: "tituloTabla" },
      { text: "Margen que quedaria", style: "tituloTabla" },
      { text: "Precio hoy", style: "tituloTabla" },
      { text: "Precio que quedaria", style: "tituloTabla" },
      { text: "Diferencia", style: "tituloTabla" },
      { text: "Por que", style: "tituloTabla" },
    ];

    const filasEjemplos = (lista.ejemplos || []).length
      ? lista.ejemplos.map((e) => {
          const esBloqueado = e.clasificacion === "SIN CALCULAR";
          const color = esBloqueado
            ? "#8a6d1f"
            : e.diferencia > 0
            ? "#1f5f3f"
            : e.diferencia < 0
            ? "#8a2d1f"
            : "black";
          return [
            { text: e.clave || e.codigo || "", style: "textoTabla" },
            { text: e.descripcion || "", style: "textoTabla" },
            { text: dinero(e.costo), style: "textoTabla", alignment: "right" },
            { text: pct(e.margenAntes), style: "textoTabla", alignment: "right" },
            {
              // El margen real con el que queda el insumo, no cuanto sube.
              text: esBloqueado ? "sin cambio" : pct(e.margenDespues),
              style: "textoTabla",
              alignment: "right",
              color,
            },
            { text: dinero(e.precioAntes), style: "textoTabla", alignment: "right" },
            {
              text: esBloqueado ? "sin cambio" : dinero(e.precioDespues),
              style: "textoTabla",
              alignment: "right",
              color,
            },
            {
              text: esBloqueado ? "-" : dineroConSigno(e.diferencia),
              style: "textoTabla",
              alignment: "right",
              color,
            },
            {
              text: esBloqueado
                ? e.motivo || ""
                : [e.origenMargen, ...(e.ajustes || []).filter((a) => a !== "ninguno")]
                    .filter(Boolean)
                    .join(" · "),
              style: "textoTabla",
            },
          ];
        })
      : [filaVacia(9, "No hay insumos con precio en esta lista")];

    return [
      {
        text: `Lista: ${lista.nombre || "(sin nombre)"}`,
        style: "tituloLista",
        // Cada lista abre pagina para poder presentarla por separado.
        ...(indice > 0 ? { pageBreak: "before" } : {}),
      },

      { text: "Configuracion", style: "seccion" },
      {
        style: "tableExample",
        table: { widths: ["20%", "26%", "24%", "30%"], body: filasConfig },
        layout: layoutTabla,
      },

      {
        text: `Como afectaria — sobre la muestra de ${lista.conEntrada || 0} insumos con precio en esta lista`,
        style: "seccion",
      },
      {
        style: "tableExample",
        table: {
          widths: ["20%", "20%", "20%", "20%", "20%"],
          body: [
            [
              tarjeta("Suben de precio", lista.suben, "#1f5f3f"),
              tarjeta("Bajan de precio", lista.bajan, "#8a2d1f"),
              tarjeta("Quedan igual", lista.iguales),
              tarjeta("No se recalculan", lista.sinCalcular, "#8a6d1f"),
              tarjeta(
                "Margen tipico",
                `${pct(lista.margenTipicoAntes)} → ${pct(lista.margenTipicoDespues)}`,
                lista.margenTipicoDespues > lista.margenTipicoAntes
                  ? "#1f5f3f"
                  : lista.margenTipicoDespues < lista.margenTipicoAntes
                  ? "#8a2d1f"
                  : "black"
              ),
            ],
          ],
        },
        layout: layoutSinBordes,
      },

      {
        text: [
          { text: "Mayor aumento: ", style: "subheader" },
          { text: textoExtremo(lista.mayorAumento, "ninguno"), style: "textoChico" },
          { text: "     Mayor baja: ", style: "subheader" },
          { text: textoExtremo(lista.mayorBaja, "ninguna"), style: "textoChico" },
        ],
        margin: [0, 2, 0, 6],
      },

      ...(lista.sinCalcular
        ? [
            { text: "Por que no se recalculan", style: "seccion" },
            {
              style: "tableExample",
              table: {
                widths: ["8%", "92%"],
                body: [
                  [
                    { text: "Cuantos", style: "tituloTabla" },
                    { text: "Motivo", style: "tituloTabla" },
                  ],
                  ...filasMotivos,
                ],
              },
              layout: layoutTabla,
            },
          ]
        : []),

      { text: "Ejemplos", style: "seccion" },
      {
        style: "tableExample",
        table: {
          headerRows: 1,
          widths: ["7%", "18%", "7%", "8%", "10%", "9%", "10%", "9%", "22%"],
          body: [encabezadoEjemplos, ...filasEjemplos],
        },
        layout: layoutTabla,
      },
    ];
  });

  const dd = {
    footer: { image: "logoBiwo", alignment: "center", width: 80 },
    content: [
      {
        style: "tableExample",
        table: {
          widths: ["18%", "52%", "30%"],
          layout: { defaultBorder: false },
          body: [
            [
              ...HeaderEmpresaCard(empresa, false, false, {
                border: [false, false, false, false],
                table: {
                  headerRows: 1,
                  widths: ["100%"],
                  body: [
                    [{ text: "Fecha de Impresión:", style: "subheader", alignment: "center" }],
                    [
                      {
                        text: moment().locale("es").format("LL"),
                        style: "textoChico",
                        alignment: "center",
                      },
                    ],
                  ],
                },
                layout: "noBorders",
              }),
            ],
          ],
        },
      },
      { text: "Simulación de Listas de Precios", style: "tableHeaderIzquierda" },
      {
        text:
          "Se simula el precio que quedaria si hoy llegara una compra al costo actual de cada insumo. " +
          "Como el costo no se mueve, toda diferencia contra el precio de hoy viene de la configuracion. " +
          "Es una simulacion: no cambia ningun precio. Los porcentajes que se muestran son SIEMPRE el " +
          "margen real del insumo, no cuanto sube o baja el precio. El margen tipico es la mediana, no el " +
          "promedio, para que un renglon con datos fuera de rango no distorsione el resumen. " +
          "Solo se analizan insumos principales de venta; las equivalencias quedan fuera.",
        style: "textoChico",
        margin: [0, 4, 0, 2],
      },
      {
        text:
          "IMPORTANTE: es una MUESTRA tomada al azar, no el catalogo completo. Los conteos de cada " +
          "lista (cuantos suben, cuantos bajan) son sobre esa muestra y sirven para ver la tendencia " +
          "y los ejemplos, no como cifra exacta del catalogo.",
        style: "aviso",
        margin: [0, 2, 0, 4],
      },
      {
        text: [
          { text: "Insumos simulados: ", style: "subheader" },
          {
            text: `${values.insumosSimulados || 0} de ${values.totalCatalogo || 0} del catalogo`,
            style: "textoChico",
          },
          { text: "     Base del margen: ", style: "subheader" },
          {
            text: values.calcularPreciosSobre ? "sobre COSTO" : "sobre VENTA",
            style: "textoChico",
          },
          { text: "     Calculo automatico global: ", style: "subheader" },
          {
            text: values.calcularPreciosAutomaticamente ? "prendido" : "apagado",
            style: "textoChico",
          },
          { text: "     Marcas excluidas: ", style: "subheader" },
          { text: `${values.marcasBloqueadas || 0}`, style: "textoChico" },
        ],
        margin: [0, 2, 0, 4],
      },
      ...(values.bloquearCosto
        ? [
            {
              text:
                "AVISO: la empresa tiene Bloquear Costo prendido, asi que hoy ninguna compra " +
                "modifica costos ni precios. Esta simulacion muestra lo que pasaria si se apagara.",
              style: "aviso",
              margin: [0, 2, 0, 6],
            },
          ]
        : []),
      ...(listas.length
        ? contenidoListas
        : [{ text: "No hay listas de precios activas.", style: "textoChico", margin: [0, 12, 0, 0] }]),
    ],
    images: {
      logo: empresa.logo.base64Completa,
      logoBiwo: logoBiwo.base64Completa,
    },
    styles: {
      tableHeaderIzquierda: { bold: true, fontSize: 16, color: "black" },
      tituloLista: { bold: true, fontSize: 13, color: "black", margin: [0, 10, 0, 2] },
      seccion: { bold: true, fontSize: 9, color: "#444444", margin: [0, 6, 0, 0] },
      subheader: { fontSize: 8, bold: true },
      textoChico: { fontSize: 8 },
      tableExample: { margin: [0, 4, 0, 4] },
      cifra: { fontSize: 17, bold: true },
      cifraEtiqueta: { fontSize: 7.5, color: "#555555" },
      aviso: { fontSize: 8.5, bold: true, color: "#8a2d1f" },
      tituloTabla: { fontSize: 8, bold: true, fillColor: "#cccccc", alignment: "center" },
      textoTabla: { fontSize: 7, italics: false },
    },
    pageSize: "LETTER",
    pageOrientation: "landscape",
    pageMargins: [20, 20, 20, 50],
  };

  return dd;
};

export default simulacionListasDePreciosPdf;
